
import Vue from 'vue';
import { Data as ArticleData, Headlines, ContentFragment as ArticleContentFragment } from '@root/modules/article/types/article';
import ContentRecipe from '@core/components/UI/ContentRecipe.vue';
import ContentFragment from '@core/components/ContentFragment.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import ArticleBodyBottom from '@root/views/article/components/ArticleBodyBottom.vue';
import ArticleHeadlinesSection from '@root/views/article/components/headlines/ArticleHeadlinesSection.vue';

import BodySkeletonLoader from '@root/views/article/components/BodySkeletonLoader.vue';
const ArticlePaywall = () => import(/* webpackChunkName: "ArticlePaywall" */ '@root/views/article/components/paywall/ArticlePaywall.vue');
const CustomerStateHandler = () => import(/* webpackChunkName: "CustomerStateHandler" */ '@customer/components/CustomerStateHandler.vue');

import getArticleMultipageBody from '@root/modules/article/utils/getArticleMultipageBody';
import ArticleBodyMultipageNavigation from '@root/views/article/components/ArticleBodyMultipageNavigation.vue';

interface Props {
  article: ArticleData;
  headlines: Headlines | [];
  isArticleFetching: boolean;
  fontSizeClass: string;
}

interface Computed {
  articleMultipageBody: Array<Record<string, any>>;
  disabledAdsList: string[];
  disableAds: boolean;
  adPlacements: Record<string, any>;
  bodyFirstAd: Record<string, any>;
  page: number;
  chunk: Record<string, any>;
}

interface Data {
  initFragmentsGroup: FragmentsGroup;
  relatedArticlesSettings: {
    minParagraphsLength: number;
    afterParagraphPosition: number;
    articlesLimit: number;
    articlesInBodyLimit: number;
    endOfArticleBody: boolean;
    disableRelatedArticles: boolean;
  };
}

interface Methods {
  getPlacement: (index: number, type: string) => string;
}

type Fragment = ArticleContentFragment;

interface FragmentsGroup {
  items: (Fragment | string)[];
  options: {
    skipAds: boolean;
    addRelatedHeadlines: boolean;
    relatedPosition: number;
  };
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    AdFormBanner,
    ArticleBodyBottom,
    ContentFragment,
    ContentRecipe,
    ArticleHeadlinesSection,
    ArticlePaywall,
    BodySkeletonLoader,
    CustomerStateHandler,
    ArticleBodyMultipageNavigation,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    headlines: {
      type: Array,
      required: true,
    },
    isArticleFetching: {
      type: Boolean,
      required: false,
      default: false,
    },
    fontSizeClass: {
      type: String,
      required: false,
      default: 'C-article__body-fs-1',
    },
  },
  data() {
    return {
      initFragmentsGroup: {
        items: [],
        options: {
          skipAds: false,
          addRelatedHeadlines: false,
          relatedPosition: 1,
        },
      },
      relatedArticlesSettings: this.$channelConfig('page').article.component.relatedArticles,
    };
  },
  computed: {
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    articleMultipageBody() {
      return getArticleMultipageBody(this.article, this.$channelConfig('page').article.component);
    },
    adPlacements() {
      const placements: Record<string, any> = this.$store.getters['adForm/getAdsPlacements'];

      return placements || {};
    },
    bodyFirstAd() {
      const placements: Record<string, any> = { desktop: null, mobile: null };

      if (this.adPlacements.bodyfirst && this.adPlacements.bodyfirst.mid.length && this.adPlacements.bodyfirst.mid[0]) {
        placements.desktop = this.adPlacements.bodyfirst.mid[0];
      }

      if (this.adPlacements.mobilebodyfirst && this.adPlacements.mobilebodyfirst.mid.length && this.adPlacements.mobilebodyfirst.mid[0]) {
        placements.mobile = this.adPlacements.mobilebodyfirst.mid[0];
      }

      return placements;
    },
    page() {
      let page = 1;

      if (this.$route.query.page !== undefined) {
        page = parseInt(this.$route.query.page as string);
      }

      return page;
    },
    chunk() {
      const index = this.page - 1;
      if (this.articleMultipageBody[index]) {
        return this.articleMultipageBody[index];
      }

      return this.articleMultipageBody[0];
    },
  },
  methods: {
    getPlacement(index, type) {
      const length = this.articleMultipageBody.length;
      const placements = this.adPlacements[type].mid;
      const loop = Math.ceil(placements.length / length);
      let placement = placements[index];
      let newIndex = null;

      // Loop again through placements
      if (index >= placements.length) {
        newIndex = index - placements.length * loop;
        placement = placements[newIndex];
      }

      return placement;
    },
  },
});
